import { useState } from 'preact/hooks';
import { SUBJECTS } from '../../lib/subjects.ts';
import { live, useStore } from '../../store/store.ts';
import { studentDisplayName } from '../../lib/student-roster.ts';
import type { SkillStatus } from '../../store/types.ts';
import { closeModal, openModal, toast } from '../../ui/ui-store.ts';

const STATUSES: readonly SkillStatus[] = ['À travailler', 'En cours', 'Acquis'];

export function SkillModal({ subject: initialSubject }: { subject?: string }) {
  const students = live(useStore((s) => s.students));

  const [subject, setSubject] = useState(initialSubject ?? (SUBJECTS[0][0] as string));
  const [status, setStatus] = useState<SkillStatus>('À travailler');
  const [title, setTitle] = useState('');
  const [studentId, setStudentId] = useState('');

  function save() {
    if (!title.trim()) {
      toast('Nomme la compétence');
      return;
    }
    useStore.getState().addSkill({ subject, status, title: title.trim(), studentId });
    closeModal();
    toast('Compétence ajoutée');
  }

  return (
    <>
      <div class="formGrid">
        <div>
          <label for="ksub">Matière</label>
          <select id="ksub" value={subject} onChange={(e) => setSubject(e.currentTarget.value)}>
            {SUBJECTS.map(([name]) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label for="kstatus">Statut</label>
          <select
            id="kstatus"
            value={status}
            onChange={(e) => setStatus(e.currentTarget.value as SkillStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="ktitle">Compétence</label>
          <input id="ktitle" value={title} onInput={(e) => setTitle(e.currentTarget.value)} />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="kstudent">Élève concerné (facultatif)</label>
          <select
            id="kstudent"
            value={studentId}
            onChange={(e) => setStudentId(e.currentTarget.value)}
          >
            <option value="">Toute la classe</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {studentDisplayName(s.name)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: '14px' }}>
        <button class="btn primary" onClick={save}>
          Enregistrer
        </button>
      </div>
    </>
  );
}

export function MeetingModal() {
  const [date, setDate] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');

  function save() {
    if (!title.trim()) {
      toast('Donne un titre à la réunion');
      return;
    }
    useStore.getState().addMeeting({ date, title, notes });
    closeModal();
    toast('Réunion enregistrée');
  }

  return (
    <>
      <div class="formGrid">
        <div>
          <label for="mdate">Date</label>
          <input id="mdate" type="date" value={date} onInput={(e) => setDate(e.currentTarget.value)} />
        </div>
        <div>
          <label for="mtitle">Titre</label>
          <input id="mtitle" value={title} onInput={(e) => setTitle(e.currentTarget.value)} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label for="mnotes">Notes / compte rendu</label>
          <textarea id="mnotes" value={notes} onInput={(e) => setNotes(e.currentTarget.value)} />
        </div>
      </div>

      <div style={{ marginTop: '14px' }}>
        <button class="btn primary" onClick={save}>
          Enregistrer
        </button>
      </div>
    </>
  );
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One task: new, being corrected, or seeded from another one.
 *
 * `taskId` edits in place. `sourceTaskId` prefills a new task from an existing
 * one, so a reminder can be repeated on another date without retyping it; the
 * copy starts not done, whatever the original's state.
 */
export function TaskModal({
  initialDue = '',
  taskId,
  sourceTaskId,
}: {
  initialDue?: string;
  taskId?: string;
  sourceTaskId?: string;
}) {
  const existing = useStore((s) => (taskId ? s.tasks.find((t) => t.id === taskId) : undefined));
  const source = useStore((s) =>
    sourceTaskId ? s.tasks.find((t) => t.id === sourceTaskId) : undefined,
  );
  const seed = existing ?? source;

  const [text, setText] = useState(seed?.text ?? '');
  const [due, setDue] = useState(seed?.due ?? initialDue);

  function save() {
    if (!text.trim()) return;

    if (existing) {
      useStore.getState().updateTask(existing.id, { text, due });
      closeModal();
      toast('Tâche modifiée');
      return;
    }

    useStore.getState().addTask(text, due);
    closeModal();
    toast(source ? 'Tâche dupliquée' : 'Tâche ajoutée');
  }

  function duplicate() {
    if (!existing) return;
    openModal('Dupliquer la tâche', <TaskModal sourceTaskId={existing.id} />);
  }

  function remove() {
    if (!existing) return;
    useStore.getState().deleteTask(existing.id);
    closeModal();
    toast('Tâche supprimée');
  }

  return (
    <>
      {source && (
        <p class="small muted" style={{ marginTop: 0 }}>
          Choisis la date de la copie. Son texte peut être adapté avant de l’enregistrer.
        </p>
      )}

      <label for="taskText">Tâche</label>
      <input
        id="taskText"
        value={text}
        placeholder="Ex. Corriger les évaluations"
        onInput={(e) => setText(e.currentTarget.value)}
      />

      <div style={{ marginTop: '10px' }}>
        <label for="taskDue">Date à retenir</label>
        <input
          id="taskDue"
          type="date"
          value={ISO_DATE.test(due) ? due : ''}
          onInput={(e) => setDue(e.currentTarget.value)}
        />
        {/* Older tasks may hold free text ("jeudi") that a date field cannot
            show; it is kept as is unless a date is picked. */}
        {due && !ISO_DATE.test(due) && (
          <div class="small muted" style={{ marginTop: '5px' }}>
            Noté sans date précise : « {due} »
          </div>
        )}
      </div>

      <div class="row" style={{ marginTop: '14px' }}>
        <button class="btn primary" onClick={save}>
          Enregistrer
        </button>
        {existing && (
          <button class="btn" onClick={duplicate}>
            ⧉ Dupliquer
          </button>
        )}
        {existing && (
          <button class="btn danger" onClick={remove}>
            Supprimer
          </button>
        )}
      </div>
    </>
  );
}

const POSTIT_COLORS = [
  ['#fff2b9', 'Jaune'],
  ['#f8dfe8', 'Rose'],
  ['#dceeff', 'Bleu'],
  ['#e4f4e7', 'Vert'],
] as const;

export function PostitModal() {
  const [text, setText] = useState('');
  const [color, setColor] = useState<string>(POSTIT_COLORS[0][0]);

  function save() {
    if (!text.trim()) return;
    useStore.getState().addPostit(text, color);
    closeModal();
    toast('Note ajoutée');
  }

  return (
    <>
      <label for="postText">Note</label>
      <textarea id="postText" value={text} onInput={(e) => setText(e.currentTarget.value)} />

      <div style={{ marginTop: '10px' }}>
        <label for="postColor">Couleur</label>
        <select id="postColor" value={color} onChange={(e) => setColor(e.currentTarget.value)}>
          {POSTIT_COLORS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginTop: '14px' }}>
        <button class="btn primary" onClick={save}>
          Enregistrer
        </button>
      </div>
    </>
  );
}
